import type { DataOption } from '@/common.types'
import type { AnchorResponseModel } from '@/shared/generated'
import type { AnchorProductRow } from '../../model/types'

import React, { useMemo } from 'react'
import { capitalize, find } from 'lodash/fp'
import {
	AlertTriangleIcon,
	FolderIcon,
	InfoIcon,
	Share2Icon,
} from 'lucide-react'
import { useTranslation } from 'react-i18next'

import {
	getAnchorsTypesChoices,
	useAnchorsQuery,
	useAnchorsRelationsByTypeQuery,
} from '@/entities/anchor'

import imgBundleAnchorType from '@/shared/assets/img/illustrations/bundle_anchor_type.jpg'
import imgFlatAnchorType from '@/shared/assets/img/illustrations/flat_anchor_type.jpg'
import imgLadderAnchorType from '@/shared/assets/img/illustrations/ladder_anchor_type.jpg'
import imgProductSetAnchorType from '@/shared/assets/img/illustrations/product_set_anchor_type.jpg'
import imgSizeAnchorType from '@/shared/assets/img/illustrations/size_anchor_type.jpg'
import MetaValue from '@/shared/components/common/MetaValue'
import { ErrorBoundary } from '@/shared/components/ErrorBoundary'
import { Operators } from '@/shared/components/query-builder'
import {
	Accordion,
	AccordionContent,
	AccordionItem,
	AccordionTrigger,
} from '@/shared/components/ui/accordion'
import { Alert, AlertDescription } from '@/shared/components/ui/alert'
import { Badge } from '@/shared/components/ui/badge'
import {
	DescriptionDetail,
	DescriptionGroup,
	DescriptionList,
	DescriptionTerm,
} from '@/shared/components/ui/description-list'
import {
	Empty,
	EmptyDescription,
	EmptyHeader,
	EmptyMedia,
	EmptyTitle,
} from '@/shared/components/ui/empty'
import { Content, Header, Layout } from '@/shared/components/ui/layout'
import {
	Sheet,
	SheetContent,
	SheetHeader,
	SheetTitle,
} from '@/shared/components/ui/sheet'
import { Skeleton } from '@/shared/components/ui/skeleton'
import {
	Tooltip,
	TooltipContent,
	TooltipTrigger,
} from '@/shared/components/ui/tooltip'

import { getAchorTypes } from '../../lib/helpers'
import AnchorProductTypeTable from './AnchorProductTypeTable'

export default function AnchorProductDetailDrawer({
	selectedSku,
	onClose,
}: {
	selectedSku: string | null
	onClose(): void
}) {
	const { t } = useTranslation()

	const filters = useMemo(() => {
		const filters = []
		const sku = selectedSku?.includes('__')
			? selectedSku?.split('__')[1]
			: selectedSku

		if (sku) {
			filters.push({
				name: 'sku',
				value: sku,
				operation: Operators.IS as const,
			})
		}
		const salesEntityId = selectedSku?.includes('__')
			? selectedSku?.split('__')[0]
			: null

		if (salesEntityId) {
			filters.push(
				{
					name: 'sales_entity_id',
					value: salesEntityId,
					operation: Operators.IS as const,
				},
				{
					name: 'optimization_group_id',
					value: selectedSku?.split('__')[2] || '',
					operation: Operators.IS as const,
				}
			)
		}

		return filters
	}, [selectedSku])
	const selectedItemQuery = useAnchorsQuery(
		{
			limit: 1,
			filters,
		},
		{
			enabled: !!selectedSku,
			select: ({ data }) => data?.[0],
		}
	)
	const handleClose = () => onClose()

	return (
		<Sheet
			modal={false}
			defaultOpen={!!selectedSku}
			open={!!selectedSku}
			onOpenChange={(open) => !open && handleClose()}
		>
			<SheetContent
				onPointerDownOutside={(e) => e.preventDefault()}
				onInteractOutside={(e) => e.preventDefault()}
				disbleOverlay
				className='inset-y-2 right-2 h-auto w-[600px] gap-0 rounded-3xl border bg-background/80 backdrop-blur-xl sm:max-w-[600px]'
			>
				<ErrorBoundary>
					{selectedItemQuery.isLoading && (
						<Layout className='h-full'>
							<Header className='flex flex-col space-y-3 px-5 py-3'>
								<HeaderSkeleton />
							</Header>
							<Content className='bg-application flex flex-col overflow-x-hidden overflow-y-auto p-5'>
								<ContentSkeleton />
							</Content>
						</Layout>
					)}
					{!selectedItemQuery.isLoading &&
						(selectedItemQuery.data ? (
							<AnchorProductDeatils selectedItem={selectedItemQuery.data} />
						) : (
							<Layout className='h-full'>
								<Content className='flex flex-col overflow-x-hidden overflow-y-auto p-5'>
									<div className='flex h-full items-center justify-center'>
										<Empty>
											<EmptyHeader>
												<EmptyMedia variant='icon'>
													<FolderIcon />
												</EmptyMedia>
												<EmptyTitle>{t('error.sku_not_found')}</EmptyTitle>
												<EmptyDescription>
													{t('error.sku_not_found_description')}
												</EmptyDescription>
											</EmptyHeader>
										</Empty>
									</div>
								</Content>
							</Layout>
						))}
				</ErrorBoundary>
			</SheetContent>
		</Sheet>
	)
}
function AnchorProductDeatils({
	selectedItem,
}: {
	selectedItem: AnchorResponseModel
}) {
	const { t } = useTranslation()

	const alerts = selectedItem?.alerts?.filter(Boolean)

	return (
		<Layout className='h-full'>
			<SheetHeader>
				<SheetTitle className='pr-8 text-base'>
					{selectedItem?.title || '-'}
				</SheetTitle>
			</SheetHeader>
			<Header className='flex flex-col space-y-3 px-4'>
				<div className='flex gap-2'>
					{!!selectedItem?.line_id && (
						<MetaValue
							label={t('product_line_id')}
							value={selectedItem.line_id}
						/>
					)}
					{!!selectedItem?.sku && (
						<MetaValue label={t('sku')} value={selectedItem.sku} />
					)}
				</div>
				<DescriptionList className='flex-start flex gap-6'>
					<DescriptionGroup>
						<DescriptionTerm>{t('app.anchors_types')}</DescriptionTerm>
						<DescriptionDetail className='flex items-center gap-1'>
							{getAchorTypes(selectedItem)
								.map(
									(value) =>
										find({ value }, getAnchorsTypesChoices(t)) as DataOption & {
											variant: string
										}
								)
								.map((type) => (
									<Badge key={type.value} variant={type.variant as any}>
										{(selectedItem[
											`${type.value}_anchor_count` as keyof AnchorResponseModel
										] as number) > 0 ? (
											<Share2Icon className='size-4' />
										) : null}
										{type.label}
									</Badge>
								))}
						</DescriptionDetail>
					</DescriptionGroup>
				</DescriptionList>
			</Header>
			<Content className='bg-application flex flex-col overflow-x-hidden overflow-y-auto'>
				<ErrorBoundary>
					<div className='p-4'>
						{!!alerts?.length && (
							<div className='mb-3 grid gap-2'>
								{alerts?.map((alert) => (
									<Alert variant='destructive' key={alert}>
										<AlertTriangleIcon />
										<AlertDescription>
											{t(`anchor_alerts.${alert}.name`, {
												defaultValue: alert,
											})}
										</AlertDescription>
									</Alert>
								))}
							</div>
						)}

						<AnchorGroups selectedItem={selectedItem} key={selectedItem.sku} />
					</div>
				</ErrorBoundary>
			</Content>
		</Layout>
	)
}

function AnchorGroups({ selectedItem }: { selectedItem: AnchorResponseModel }) {
	const { t } = useTranslation()

	const anchorsGroups = useAnchorsRelationsByTypeQuery(selectedItem, {
		limit: 1000,
	})
	const openAccordionItems = useMemo(
		() =>
			anchorsGroups?.data
				?.filter(({ rows }) => !!rows.length)
				.map(({ type }) => type) || [],
		[anchorsGroups.data]
	)

	return anchorsGroups?.isLoading ? (
		<ContentSkeleton />
	) : (
		<Accordion
			type='multiple'
			className='w-full'
			defaultValue={openAccordionItems}
		>
			{anchorsGroups?.data?.map(({ type, rows }) => {
				const rowsData = rows?.map((item): AnchorProductRow => ({
					sku: item.sku!,
					title: item.title as string,
					is_anchor: item.is_anchor,
					is_current: item.sku === selectedItem.sku,
					add_idx: item[`${type}_anchor_add` as ItemKey] as number,
					mul_idx: item[`${type}_anchor_mul` as ItemKey] as number,
					sales_entity_id: item.sales_entity_id,
					optimization_group_id: item.optimization_group_id,
				}))

				return (
					<AccordionItem key={type} value={type}>
						<AccordionTrigger disabled={!rows.length}>
							<div className='flex items-center gap-2'>
								{t(`app.${type}_anchor`, { defaultValue: capitalize(type) })}
								<Tooltip>
									<TooltipTrigger asChild>
										<InfoIcon className='size-3' />
									</TooltipTrigger>
									<TooltipContent
										collisionBoundary={
											document.getElementsByClassName('drawer-wrapper')[0]
										}
									>
										<div className='my-1 flex w-[260px] flex-col'>
											{t(`app.${type}_anchor.description`)}
											<img
												src={ANCHOR_TYPE_IMAGES[type]}
												alt={t(`app.${type}_anchor`)}
												className='mt-2 rounded-xs'
											/>
										</div>
									</TooltipContent>
								</Tooltip>
							</div>
						</AccordionTrigger>
						{!!rows.length && (
							<AccordionContent>
								<AnchorProductTypeTable rows={rowsData} />
							</AccordionContent>
						)}
					</AccordionItem>
				)
			})}
		</Accordion>
	)
}
const HeaderSkeleton = () => (
	<>
		<Skeleton className='mb-5 h-5 w-44 rounded-lg' />
		<Skeleton className='mb-2 h-4 w-32 rounded-lg' />
		<Skeleton className='mb-2 h-4 w-32 rounded-lg' />
		<Skeleton className='mb-10 h-5 w-full rounded-lg' />
	</>
)
const ContentSkeleton = () => (
	<div className='space-y-5'>
		<Skeleton className='h-5 w-28 rounded-lg' />
		<Skeleton className='h-12 w-full rounded-lg' />
		<Skeleton className='h-5 w-28 rounded-lg' />
		<Skeleton className='h-12 w-full rounded-lg' />
		<Skeleton className='h-5 w-28 rounded-lg' />
		<Skeleton className='h-12 w-full rounded-lg' />
		<Skeleton className='h-5 w-28 rounded-lg' />
		<Skeleton className='h-12 w-full rounded-lg' />
		<Skeleton className='h-5 w-28 rounded-lg' />
		<Skeleton className='h-12 w-full rounded-lg' />
	</div>
)
type ItemKey = keyof AnchorResponseModel
const ANCHOR_TYPE_IMAGES: Record<string, string> = {
	bundle: imgBundleAnchorType,
	flat: imgFlatAnchorType,
	ladder: imgLadderAnchorType,
	product_set: imgProductSetAnchorType,
	size: imgSizeAnchorType,
}
